import { z } from 'zod';

/**
 * The tracker snapshot the backend keeps per team (ADR 0011): every machine, preview and board reads the queue
 * through it, so the tracker is read at most once per team per `FLEET_QUEUE_FLOOR_MS`, however many ask.
 *
 * The backend holds no lane config: it returns the team's open issues whole, and the reader filters them by its own
 * lanes' `pick-from`.
 */

/** No new tracker read for a team within this long of the last one. */
export const FLEET_QUEUE_FLOOR_MS = 30_000;

/** The most tickets one comments read may name. */
export const FLEET_QUEUE_COMMENTS_MAX_TICKETS = 50;

/** The trackers the backend reads. `command` and `github` queues stay on the machine (ADR 0011, not decided). */
export const FleetQueueTrackerSchema = z.enum(['linear']);
export type FleetQueueTracker = z.infer<typeof FleetQueueTrackerSchema>;

/** A team by the tracker's own name (`immoOS`, `LandIQ - Data`). */
const TeamSchema = z.string().min(1).max(128);

export const ReadFleetQueueBodySchema = z.object({ tracker: FleetQueueTrackerSchema, team: TeamSchema });
export type ReadFleetQueueBody = z.infer<typeof ReadFleetQueueBodySchema>;

/** A workflow state, in the tracker's own board order. */
export const FleetQueueStateSchema = z.object({
  name: z.string(),
  /** Linear's state type: `triage`, `backlog`, `unstarted`, `started`, `completed`, `canceled`. */
  type: z.string(),
});
export type FleetQueueState = z.infer<typeof FleetQueueStateSchema>;

/** A tracker blocker as the relation read returns it (IMM-1419): what the board's blocker row shows of it. */
export const FleetQueueBlockerSchema = z.object({
  /** `IMM-123`. */
  identifier: z.string(),
  title: z.string(),
  url: z.url(),
  /** The state's exact name. */
  state: z.string(),
});
export type FleetQueueBlocker = z.infer<typeof FleetQueueBlockerSchema>;

export const FleetQueueIssueSchema = z.object({
  /** `IMM-815`. */
  identifier: z.string(),
  title: z.string(),
  url: z.url(),
  /** The state's exact name. */
  state: z.string(),
  /** Linear's priority: 0 none, 1 urgent … 4 low. */
  priority: z.number().int().min(0).max(4),
  /** Label names, exactly as the tracker spells them. */
  labels: z.array(z.string()),
  createdAt: z.iso.datetime(),
  /** When the issue entered its current state; `null` when the tracker's history does not say. */
  stateEnteredAt: z.iso.datetime().nullable(),
  assignee: z.string().nullable(),
  /**
   * The issues the tracker records as blocking this one that are still open (neither `completed` nor `canceled`),
   * `IMM-123`. Absent from a snapshot read before the backend read relations.
   */
  blockedBy: z.array(z.string()).optional(),
  /**
   * `blockedBy`'s issues, in its order, with their title, URL and state (IMM-1419). Absent from a snapshot read
   * before the backend read them.
   */
  blockers: z.array(FleetQueueBlockerSchema).optional(),
});
export type FleetQueueIssue = z.infer<typeof FleetQueueIssueSchema>;

export const ReadFleetQueueResponseSchema = z.object({
  tracker: FleetQueueTrackerSchema,
  team: TeamSchema,
  /** When the backend last read the tracker for this team — at most `FLEET_QUEUE_FLOOR_MS` behind a fresh read. */
  fetchedAt: z.iso.datetime(),
  /** The team's workflow states in board order: a board's columns. */
  states: z.array(FleetQueueStateSchema),
  /** Every open issue: the state type is neither `completed` nor `canceled`. */
  issues: z.array(FleetQueueIssueSchema),
});
export type ReadFleetQueueResponse = z.infer<typeof ReadFleetQueueResponseSchema>;

/** The last comments of a few tickets — the parked and running rows a preview shows a reason for. */
export const ReadFleetQueueCommentsBodySchema = z.object({
  tracker: FleetQueueTrackerSchema,
  team: TeamSchema,
  tickets: z.array(z.string().min(1).max(64)).min(1).max(FLEET_QUEUE_COMMENTS_MAX_TICKETS),
});
export type ReadFleetQueueCommentsBody = z.infer<typeof ReadFleetQueueCommentsBodySchema>;

export const FleetQueueCommentSchema = z.object({
  body: z.string(),
  createdAt: z.iso.datetime(),
  author: z.string().nullable(),
});
export type FleetQueueComment = z.infer<typeof FleetQueueCommentSchema>;

export const ReadFleetQueueCommentsResponseSchema = z.object({
  /** Newest first, at most ten per ticket. A ticket the tracker does not know is absent. */
  comments: z.record(z.string(), z.array(FleetQueueCommentSchema)),
});
export type ReadFleetQueueCommentsResponse = z.infer<typeof ReadFleetQueueCommentsResponseSchema>;
